import { useState, useRef, useCallback, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Search, Upload, Image as ImageIcon, X, FolderOpen } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { Modal } from '@/components/ui/Modal';
import { Input } from '@/components/ui/Input';
import { Spinner } from '@/components/ui/Spinner';
import { useToast } from '@/hooks/useToast';
import { cn } from '@/lib/utils';
import { apiClient } from '@/services/apiClient';
import {
  uploadMarketingPhoto,
  getPhotoFolders,
  isAllowedPhotoType,
  photoTypeRejectionReason,
  PHOTO_ACCEPT_ATTR,
} from '@/services/marketing.service';
import {
  MMS_MAX_BYTES,
  isMmsSendableUrl,
  mmsMediaRejectionReason,
} from './sms-editor-constants';

interface Photo {
  id: number;
  url: string;
  originalFilename: string;
  altText: string | null;
  folder: string | null;
  /** True when the image comes from the master account rather than this one. */
  shared?: boolean;
  fileSize: number;
  width: number;
  height: number;
  createDate: string;
  searchTerms: string;
}

interface SmsPhotoPickerProps {
  open: boolean;
  onClose: () => void;
  onSelect: (url: string, fileSize?: number) => void;
  /** Extra className for the Modal wrapper (e.g. to raise z-index) */
  wrapperClassName?: string;
}

function formatBytes(bytes: number): string {
  if (bytes === 0) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return `${(bytes / Math.pow(k, i)).toFixed(1)} ${sizes[i]}`;
}

export default function SmsPhotoPicker({ open, onClose, onSelect, wrapperClassName }: SmsPhotoPickerProps) {
  const toast = useToast();
  const qc = useQueryClient();
  const fileRef = useRef<HTMLInputElement>(null);

  const [tab, setTab] = useState<'browse' | 'upload'>('browse');

  // Browse state
  const [search, setSearch] = useState('');
  const [folder, setFolder] = useState('');
  /** all | mine (this account's own) | shared (from the master account). */
  const [scope, setScope] = useState<'all' | 'mine' | 'shared'>('all');

  // Upload state
  const [uploadFile, setUploadFile] = useState<File | null>(null);
  const [uploadPreview, setUploadPreview] = useState('');
  const [uploadAlt, setUploadAlt] = useState('');
  const [uploadFolder, setUploadFolder] = useState('general');
  // Off by default — an MMS image is usually a one-off, and saving every one of
  // them is what filled the library with campaign clutter. Unchecked, the file
  // goes to S3 and is used on this message only.
  const [saveToLibrary, setSaveToLibrary] = useState(false);
  const [uploadProgress, setUploadProgress] = useState<number | null>(null);
  const [dragOver, setDragOver] = useState(false);

  const { data: allPhotos, isLoading: photosLoading } = useQuery({
    queryKey: ['marketing', 'photos'],
    queryFn: async () => {
      // No all_accounts flag: the API already returns this account's own photos
      // plus the ones the master account shares.
      const res = await apiClient.get('/marketing/photos');
      return res.data.data as Photo[];
    },
    enabled: open,
  });

  const { data: folders } = useQuery({
    queryKey: ['marketing', 'photo-folders'],
    // Shares its key with PhotoLibraryPage, so both must read the same shape.
    queryFn: () => getPhotoFolders(),
    enabled: open,
  });

  const q = search.trim().toLowerCase();
  const photos = useMemo(() => {
    let result = allPhotos ?? [];
    if (folder) result = result.filter((p) => p.folder === folder);
    // The list is this account's own photos plus the ones the master account
    // shares, so `shared` alone separates the two — no account id needed.
    if (scope === 'mine') result = result.filter((p) => !p.shared);
    else if (scope === 'shared') result = result.filter((p) => p.shared);
    if (q) result = result.filter((p) => {
      const haystack = `${p.originalFilename ?? ''} ${p.altText ?? ''} ${p.folder ?? ''} ${p.searchTerms ?? ''}`.toLowerCase();
      return haystack.includes(q);
    });
    return result;
  }, [allPhotos, folder, scope, q]);

  const uploadMut = useMutation({
    mutationFn: async () => {
      if (!uploadFile) throw new Error('No file');
      return uploadMarketingPhoto(uploadFile, {
        altText: uploadAlt,
        folder: uploadFolder,
        saveToLibrary,
        onProgress: (pct) => setUploadProgress(pct),
      });
    },
    onSuccess: (photo) => {
      // Only a library save changes what the Browse tab would show.
      if (saveToLibrary) {
        qc.invalidateQueries({ queryKey: ['marketing', 'photos'] });
        qc.invalidateQueries({ queryKey: ['marketing', 'photo-folders'] });
      }
      if (photo?.url) {
        onSelect(photo.url, photo.fileSize ?? uploadFile?.size ?? 0);
      }
      const saved = saveToLibrary;
      resetUpload();
      toast.success(saved ? 'Photo uploaded & added to the library' : 'Photo uploaded & added');
    },
    onError: () => {
      setUploadProgress(null);
      toast.error('Upload failed');
    },
  });

  const resetUpload = () => {
    setUploadFile(null);
    setUploadPreview('');
    setUploadAlt('');
    setUploadFolder('general');
    setSaveToLibrary(false);
    setUploadProgress(null);
  };

  const processFile = (file: File) => {
    if (file.size > MMS_MAX_BYTES) {
      toast.error(`File exceeds 5 MB limit (${formatBytes(file.size)})`);
      return;
    }
    if (!file.type.startsWith('image/')) {
      toast.error('Only image files are allowed');
      return;
    }
    // The same rule as the Photo Library page, because this posts to the same
    // endpoint: a WebP, BMP or TIFF is re-encoded to JPEG on the way in, so what
    // comes back is already sendable and there is nothing to warn about. Refusing
    // one here would only mean a file you can upload on one screen and not the
    // other. What is still refused is what cannot be converted at all — HEIC,
    // AVIF, SVG — since GD has no decoder for those.
    //
    // The Browse tab is the strict one, and has to be: picking a library image
    // attaches its existing URL, and the WebPs uploaded before this conversion
    // existed are still WebP.
    //
    // The accept attribute does not cover a dropped file, so this is the check
    // that actually runs for drag-and-drop.
    if (!isAllowedPhotoType(file)) {
      toast.error(photoTypeRejectionReason(file));
      return;
    }
    setUploadFile(file);
    const reader = new FileReader();
    reader.onload = () => setUploadPreview(reader.result as string);
    reader.readAsDataURL(file);
  };

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);
    if (e.dataTransfer.files.length > 0) processFile(e.dataTransfer.files[0]);
  }, []);

  const handleFileInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) processFile(e.target.files[0]);
  };

  const handleClose = () => {
    resetUpload();
    setSearch('');
    setFolder('');
    setTab('browse');
    onClose();
  };

  return (
    // size xl (max-w-4xl, 896px) matches the email picker's 900px — the grid was
    // cramped at lg and the two pickers sat at noticeably different widths.
    <Modal isOpen={open} onClose={handleClose} title="Photo Library" size="xl" wrapperClassName={wrapperClassName}>
      <div className="flex flex-col" style={{ minHeight: '480px' }}>
        {/* Tab bar */}
        <div className="flex border-b border-slate-200 mb-3">
          <button
            onClick={() => setTab('browse')}
            className={cn(
              'px-4 py-2 text-sm font-medium border-b-2 transition-colors',
              tab === 'browse'
                ? 'border-sky-500 text-sky-600'
                : 'border-transparent text-slate-500 hover:text-slate-700',
            )}
          >
            <FolderOpen className="h-4 w-4 inline mr-1.5 -mt-0.5" />
            Browse Library
          </button>
          <button
            onClick={() => setTab('upload')}
            className={cn(
              'px-4 py-2 text-sm font-medium border-b-2 transition-colors',
              tab === 'upload'
                ? 'border-sky-500 text-sky-600'
                : 'border-transparent text-slate-500 hover:text-slate-700',
            )}
          >
            <Upload className="h-4 w-4 inline mr-1.5 -mt-0.5" />
            Upload New
          </button>
        </div>

        {/* Browse tab */}
        {tab === 'browse' && (
          <div className="flex-1 flex flex-col">
            <div className="flex gap-2 mb-3">
              <div className="relative flex-1">
                <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                <input
                  className="w-full pl-8 pr-3 py-2 text-sm border border-slate-200 rounded-md focus:border-sky-400 focus:outline-none"
                  placeholder="Search photos..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                />
              </div>
              <select
                className="px-3 py-2 text-sm border border-slate-200 rounded-md bg-white focus:border-sky-400 focus:outline-none"
                value={folder}
                onChange={(e) => setFolder(e.target.value)}
                aria-label="Filter by folder"
              >
                <option value="">All Folders</option>
                {folders?.map((f) => (
                  <option key={f.name} value={f.name}>
                    {f.name} ({f.count})
                  </option>
                ))}
              </select>
              <select
                className="px-3 py-2 text-sm border border-slate-200 rounded-md bg-white focus:border-sky-400 focus:outline-none"
                value={scope}
                onChange={(e) => setScope(e.target.value as 'all' | 'mine' | 'shared')}
                aria-label="Filter by owner"
              >
                <option value="all">All Photos</option>
                <option value="mine">This Account</option>
                <option value="shared">Shared</option>
              </select>
            </div>

            <div className="flex-1 overflow-y-auto" style={{ maxHeight: '360px' }}>
              {photosLoading ? (
                <div className="flex items-center justify-center h-40">
                  <Spinner className="h-6 w-6" />
                </div>
              ) : !photos?.length ? (
                <div className="flex flex-col items-center justify-center h-40 text-slate-400">
                  <ImageIcon className="h-8 w-8 mb-2" />
                  <p className="text-sm">No photos found</p>
                </div>
              ) : (
                // Six across, matching the email picker now that both modals
                // are the same width.
                <div className="grid grid-cols-6 gap-2">
                  {photos.map((p) => {
                    // Images uploaded before the library started converting are
                    // still WebP. Nothing about the thumbnail would tell you that,
                    // and picking one is how a campaign gets rejected outright — so
                    // it is dimmed, badged, and says why rather than failing later.
                    const sendable = isMmsSendableUrl(p.url);

                    return (
                    <button
                      key={p.id}
                      onClick={() => {
                        if (!sendable) {
                          toast.error(mmsMediaRejectionReason(p.url));
                          return;
                        }
                        onSelect(p.url, p.fileSize);
                        handleClose();
                      }}
                      title={sendable ? p.originalFilename : mmsMediaRejectionReason(p.url)}
                      aria-disabled={!sendable}
                      className={cn(
                        'group relative aspect-square rounded-lg overflow-hidden border transition-all',
                        sendable
                          ? 'border-slate-200 hover:border-sky-400 hover:ring-2 hover:ring-sky-100'
                          : 'border-slate-200 cursor-not-allowed',
                      )}
                    >
                      <img
                        src={p.url}
                        alt={p.altText || p.originalFilename}
                        className={cn(
                          'w-full h-full object-cover',
                          !sendable && 'opacity-40 grayscale',
                        )}
                        loading="lazy"
                      />
                      {!sendable && (
                        <span className="absolute top-1 left-1 px-1 py-0.5 rounded bg-slate-900/75 text-white text-[9px] font-semibold uppercase tracking-wide">
                          Can't send
                        </span>
                      )}
                      <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors flex items-end">
                        <div className="hidden group-hover:block w-full px-1.5 py-1 bg-black/60 text-white text-[10px] truncate">
                          {p.originalFilename}
                        </div>
                      </div>
                    </button>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Upload tab */}
        {tab === 'upload' && (
          <div className="flex-1 flex flex-col gap-4">
            {!uploadFile ? (
              <div
                onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
                onDragLeave={() => setDragOver(false)}
                onDrop={handleDrop}
                className={cn(
                  'flex-1 flex flex-col items-center justify-center border-2 border-dashed rounded-lg cursor-pointer transition-colors min-h-[200px]',
                  dragOver
                    ? 'border-sky-400 bg-sky-50'
                    : 'border-slate-300 hover:border-slate-400 bg-slate-50',
                )}
                onClick={() => fileRef.current?.click()}
              >
                <Upload className="h-10 w-10 text-slate-400 mb-3" />
                <p className="text-sm font-medium text-slate-600 mb-1">
                  Drag & drop an image here, or click to browse
                </p>
                <p className="text-xs text-slate-400">PNG, JPG, GIF, WebP · Max file size: 5 MB</p>
                <p className="text-[11px] text-slate-400 mt-0.5">
                  A WebP is converted to JPEG automatically, since picture messaging cannot
                  carry one. HEIC is not supported.
                </p>
                <input
                  ref={fileRef}
                  type="file"
                  accept={PHOTO_ACCEPT_ATTR}
                  className="hidden"
                  onChange={handleFileInput}
                />
              </div>
            ) : (
              <div className="flex gap-4 flex-1">
                {/* Half the modal, image centred in it — big enough to judge the
                    picture before committing to it. */}
                <div className="w-1/2 flex-shrink-0 min-w-0 flex flex-col items-center text-center">
                  <div className="w-full aspect-square rounded-lg overflow-hidden border border-slate-200 bg-slate-50 relative">
                    <img src={uploadPreview} alt="Preview" className="w-full h-full object-contain" />
                    <button
                      onClick={resetUpload}
                      className="absolute top-1 right-1 w-6 h-6 bg-black/60 text-white rounded-full flex items-center justify-center hover:bg-black/80"
                    >
                      <X className="h-3.5 w-3.5" />
                    </button>
                  </div>
                  <p className="text-xs text-slate-500 mt-1.5 truncate max-w-full">{uploadFile.name}</p>
                  <p className="text-xs text-slate-400">{formatBytes(uploadFile.size)}</p>
                </div>

                <div className="flex-1 flex flex-col gap-3">
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">Alt Text</label>
                    <Input
                      value={uploadAlt}
                      onChange={(e) => setUploadAlt(e.target.value)}
                      placeholder="Describe this image..."
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">Folder</label>
                    <select
                      className="w-full px-3 py-2 text-sm border border-slate-200 rounded-md bg-white focus:border-sky-400 focus:outline-none disabled:bg-slate-50 disabled:text-slate-400"
                      value={uploadFolder}
                      disabled={!saveToLibrary}
                      onChange={(e) => setUploadFolder(e.target.value)}
                    >
                      {/* "general" is one of the nine system folders the API always
                          returns, so it needs no hardcoded option of its own. */}
                      {folders?.map((f) => (
                        <option key={f.name} value={f.name}>
                          {f.name}
                        </option>
                      ))}
                    </select>
                  </div>

                  <label className="flex items-start gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      className="mt-0.5 h-4 w-4 rounded border-slate-300 text-sky-600 focus:ring-sky-400"
                      checked={saveToLibrary}
                      onChange={(e) => setSaveToLibrary(e.target.checked)}
                    />
                    <span className="text-sm text-slate-700">
                      Add to Photo Library
                      <span className="block text-xs text-slate-500">
                        {saveToLibrary
                          ? 'Saved to this account only — other accounts will not see it.'
                          : 'Off: the image is uploaded and used here only, not kept in the library.'}
                      </span>
                    </span>
                  </label>

                  {uploadProgress !== null && (
                    <div className="w-full bg-slate-100 rounded-full h-2.5">
                      <div
                        className="bg-sky-500 h-2.5 rounded-full transition-all duration-300"
                        style={{ width: `${uploadProgress}%` }}
                      />
                    </div>
                  )}

                  <Button
                    className="mt-auto"
                    onClick={() => uploadMut.mutate()}
                    disabled={uploadMut.isPending}
                  >
                    {uploadMut.isPending ? 'Uploading...' : 'Upload & Insert'}
                  </Button>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </Modal>
  );
}
